/**
 * InlineFilterEdit component
 * Inline editable filter with facet selector, operator dropdown, and values autocomplete
 */

import { useState, useMemo, useEffect, MouseEvent } from 'react';

import {
  Box,
  CircularProgress,
  MenuItem,
  Popover,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import { useTranslation } from 'react-i18next';

import styles from './InlineFilterEdit.module.scss';
import { FilterValueAutocomplete } from '../FilterValueAutocomplete';
import { IconButton } from '@/components/ui/IconButton';
import { uiIcons } from '@/constants/uiIcons';
import {
  useFacetOptions,
  useFacetCounts,
  useFacetOptionsByIds,
} from '@/hooks/filters';
import type { FacetOption } from '@/hooks/filters/types';
import type { FilterClause, Operator, FilterSet } from '@/lib/filter-dsl';
import type { ResourceConfig } from '@/lib/resource-config';

// Extended type for internal use - marks options that are loading
interface FacetOptionWithLoadingState extends FacetOption {
  isLoading?: boolean;
}

interface InlineFilterEditProps {
  clause: FilterClause;
  config: ResourceConfig;
  filterSet: FilterSet;
  /**
   * Optional search query to filter facet counts.
   * See FilterBar.tsx for rationale on why we use prop drilling instead of store.
   */
  searchQuery?: string;
  onDelete: () => void;
  onUpdate: (clause: FilterClause) => void;
}

export const InlineFilterEdit = ({
  clause,
  config,
  filterSet,
  searchQuery,
  onDelete,
  onUpdate,
}: InlineFilterEditProps) => {
  const { t } = useTranslation('views');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [page, setPage] = useState(0);

  const facetConfig = config.facets[clause.facet];

  // Debounce search term to reduce API calls while typing
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchTerm(value);
        setPage(0); // Reset pagination when search changes
      }, 300),
    [],
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSetSearch.clear();
    };
  }, [debouncedSetSearch]);

  // Fetch counts - exclude the current facet to show counts relative to other filters
  const { data: counts } = useFacetCounts({
    config,
    filterSet,
    excludeFacet: clause.facet,
    query: searchQuery || undefined,
  });

  // Calculate total count for this facet (for "is not" reversal)
  const totalCount = useMemo(() => {
    if (!counts?.[clause.facet]) return 0;
    const facetCounts = counts[clause.facet];
    return Object.values(facetCounts).reduce((sum, count) => sum + count, 0);
  }, [counts, clause.facet]);

  // Fetch available options for this facet
  const {
    data: paginatedOptions,
    isLoading,
    isFetching,
  } = useFacetOptions({
    config,
    facetKey: clause.facet,
    searchTerm: debouncedSearchTerm,
    page,
  });

  const options = paginatedOptions?.options || [];

  // Identify which selected values are missing from current options
  // (need to fetch their labels separately)
  const missingIds = useMemo(() => {
    if (!facetConfig || facetConfig.valueSource.kind !== 'collection') {
      return [];
    }

    const optionSlugs = new Set(options.map((opt) => opt.slug));
    return clause.values.filter((slug) => !optionSlugs.has(slug));
  }, [clause.values, options, facetConfig]);

  // Fetch missing options by their IDs
  const {
    data: missingOptions = [],
    isSuccess: missingOptionsLoaded,
    isLoading: missingOptionsLoading,
  } = useFacetOptionsByIds({
    config,
    facetKey: clause.facet,
    ids: missingIds,
  });

  // Merge fetched options with paginated options
  const allFetchedOptions = useMemo(() => {
    const optionsMap = new Map(options.map((opt) => [opt.slug, opt]));

    // Add missing options that were fetched by ID
    for (const opt of missingOptions) {
      if (!optionsMap.has(opt.slug)) {
        optionsMap.set(opt.slug, opt);
      }
    }

    return Array.from(optionsMap.values());
  }, [options, missingOptions]);

  // Merge counts into options
  // If operator is "not", reverse the count (total - count)
  const optionsWithCounts = useMemo(() => {
    if (!counts) {
      return allFetchedOptions;
    }
    const facetCounts = counts[clause.facet] || {};
    const isNegated = clause.operator === 'not';

    return allFetchedOptions.map((opt) => {
      // Determine count key:
      // - If untranslatedLabel exists, use it (for translated facets with facetField like StatusName)
      // - If facetField exists but no untranslatedLabel, use label (for non-translated facets like CustomerName)
      // - Otherwise use slug (for facets without facetField)
      const countKey = opt.untranslatedLabel
        ? opt.untranslatedLabel
        : facetConfig?.facetField
          ? opt.label
          : opt.slug;
      const rawCount = facetCounts[countKey] || 0;
      const displayCount = isNegated ? totalCount - rawCount : rawCount;

      return {
        ...opt,
        count: displayCount,
      };
    });
  }, [
    allFetchedOptions,
    counts,
    clause.facet,
    clause.operator,
    totalCount,
    facetConfig,
  ]);

  // Build selected value objects from slugs
  // Now that we fetch missing options, we should have all labels available
  const selectedValueObjects = useMemo<FacetOptionWithLoadingState[]>(() => {
    // Guard: if facetConfig is not found, return empty array.
    // This prevents accessing facetConfig.valueSource.kind before the main null check.
    // All hooks must run before we can return null from the component (React Rules of Hooks).
    if (!facetConfig) {
      return [];
    }

    const facetCounts = counts?.[clause.facet] || {};
    const isNegated = clause.operator === 'not';
    const optionsMap = new Map(optionsWithCounts.map((opt) => [opt.slug, opt]));

    return clause.values
      .map((slug) => {
        // Try to find the full option object first (has proper label and reversed count)
        const existingOption = optionsMap.get(slug);
        if (existingOption) {
          return existingOption;
        }

        // Fallback: create a basic option with slug as label and count
        // This should rarely happen now since we fetch missing options
        const rawCount = facetCounts[slug] || 0;
        const displayCount = isNegated ? totalCount - rawCount : rawCount;

        let label = slug;
        let isLoading = false;

        if (facetConfig.valueSource.kind === 'enum') {
          // Format enum labels: true->Yes, false->No, etc.
          if (slug === 'true') label = t('Yes');
          else if (slug === 'false') label = t('No');
          else label = slug.charAt(0).toUpperCase() + slug.slice(1);
        } else if (facetConfig.valueSource.kind === 'collection') {
          // For collection-based values, return null if fetch completed but ID not found
          // This will be filtered out below
          if (missingOptionsLoaded && missingIds.includes(slug)) {
            return null; // Invalid ID - will be filtered out
          }
          // Still loading - mark as loading
          if (missingOptionsLoading && missingIds.includes(slug)) {
            isLoading = true;
            label = slug; // Keep slug as label, but mark as loading
          } else {
            label = slug; // Fallback to slug if not loading
          }
        }

        return {
          id: slug, // Fallback: use slug as id when we don't have the full option
          slug,
          label,
          count: displayCount,
          isLoading,
        };
      })
      .filter((opt): opt is FacetOptionWithLoadingState => opt !== null); // Filter out invalid IDs
  }, [
    clause.values,
    optionsWithCounts,
    counts,
    clause.facet,
    clause.operator,
    totalCount,
    facetConfig,
    t,
    missingOptionsLoaded,
    missingOptionsLoading,
    missingIds,
  ]);

  // Automatically remove invalid IDs from FilterSet after fetch completes
  useEffect(() => {
    if (
      missingOptionsLoaded &&
      facetConfig?.valueSource.kind === 'collection' &&
      missingIds.length > 0
    ) {
      // Find which missing IDs were not found (invalid)
      const fetchedSlugs = new Set(missingOptions.map((opt) => opt.slug));
      const invalidIds = missingIds.filter((id) => !fetchedSlugs.has(id));

      if (invalidIds.length > 0) {
        // Remove invalid IDs from clause values
        const validValues = clause.values.filter(
          (slug) => !invalidIds.includes(slug),
        );

        // Only update if something changed to avoid infinite loops
        if (validValues.length !== clause.values.length) {
          onUpdate({
            ...clause,
            values: validValues,
          });
        }
      }
    }
  }, [
    missingOptionsLoaded,
    missingIds,
    missingOptions,
    clause,
    onUpdate,
    facetConfig,
  ]);

  // All available options = current page options + selected values not in current page
  // Deduplicate by id to avoid showing the same option twice
  const allAvailableOptions = useMemo(() => {
    const optionsMap = new Map<string, FacetOption>();

    // Add all fetched options first (these have proper labels and counts)
    for (const opt of optionsWithCounts) {
      // Extra safeguard: skip if id is empty or undefined
      if (opt.id) {
        optionsMap.set(opt.id, opt);
      }
    }

    // Add any selected values that aren't in the current page
    for (const sel of selectedValueObjects) {
      if (sel.id && !optionsMap.has(sel.id)) {
        optionsMap.set(sel.id, sel);
      }
    }

    return Array.from(optionsMap.values());
  }, [optionsWithCounts, selectedValueObjects]);

  // Guard: return null if facet config is not found
  if (!facetConfig) {
    return null;
  }

  // Update operator (is/not)
  const handleOperatorChange = (newOperator: Operator) => {
    onUpdate({
      ...clause,
      operator: newOperator,
    });
  };

  // Update selected values
  const handleValuesChange = (newValues: FacetOption[]) => {
    const newSlugs = newValues.map((v) => v.slug);

    onUpdate({
      ...clause,
      values: newSlugs,
    });

    // Clear search term after selection
    if (newSlugs.length > clause.values.length) {
      setSearchTerm('');
    }
  };

  // Handle lazy loading
  const handleLoadMore = () => {
    if (paginatedOptions?.hasMore && !isLoading && !isFetching) {
      setPage((prev) => prev + 1);
    }
  };

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleValuesClick = (e: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle Escape - close the popover
  const handleEscape = () => {
    handleClose();
  };

  const popoverIsOpen = Boolean(anchorEl);
  const id = popoverIsOpen ? `values-popover-${clause.facet}` : undefined;

  return (
    <Stack
      direction="row"
      alignItems="stretch"
      className={styles.InlineFilterEdit}
      gap={0}
    >
      {/* Facet name (read-only for now, could be made editable) */}
      <Box className={styles.Attribute}>
        <Typography
          variant="label"
          data-automation-id={`inline-filter-edit-facet-label-${clause.facet}`}
        >
          {facetConfig.label}
        </Typography>
      </Box>

      {/* Operator dropdown */}
      <Select
        value={clause.operator}
        onChange={(e) => handleOperatorChange(e.target.value as Operator)}
        size="small"
        aria-label={t('FilterOperator')}
        className={styles.Operator}
        IconComponent={() => null}
        data-automation-id={`inline-filter-edit-operator-select-${clause.facet}`}
      >
        <MenuItem value="is">{t('Is')}</MenuItem>
        <MenuItem value="not">{t('IsNot')}</MenuItem>
      </Select>

      {/* Values autocomplete */}
      <Box className={styles.ValuesContainer}>
        <Tooltip
          title={
            selectedValueObjects.length > 1
              ? selectedValueObjects.map((v) => v.label).join(', ')
              : ''
          }
        >
          <button
            type="button"
            className={styles.ValuesButton}
            aria-controls={id}
            aria-expanded={popoverIsOpen ? 'true' : 'false'}
            onClick={handleValuesClick}
            data-automation-id={`inline-filter-edit-values-button-${clause.facet}`}
          >
            {selectedValueObjects.length === 0 && t('ChooseValues')}
            {selectedValueObjects.length > 0 && (
              <>
                {selectedValueObjects[0]?.isLoading ? (
                  <CircularProgress size={14} sx={{ mr: 0.5 }} />
                ) : (
                  selectedValueObjects[0]?.label
                )}
                {selectedValueObjects.length > 1 &&
                  ` + ${selectedValueObjects.length - 1}`}
              </>
            )}
          </button>
        </Tooltip>

        <Popover
          id={id}
          open={popoverIsOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <FilterValueAutocomplete
            options={allAvailableOptions}
            value={selectedValueObjects}
            inputValue={searchTerm}
            onChange={(_, newValue) => handleValuesChange(newValue)}
            onInputChange={(_, value, reason) => {
              // Only update search term when user is typing
              if (reason === 'input' || reason === 'clear') {
                setSearchTerm(value); // Update UI immediately
                debouncedSetSearch(value); // Debounced API call
              }
            }}
            onLoadMore={handleLoadMore}
            hasMore={paginatedOptions?.hasMore}
            loading={isLoading || isFetching}
            placeholder={t('SearchPlaceholder', {
              facet: facetConfig?.label || t('Values'),
            })}
            showCounts={facetConfig.facetBy !== false}
            autoFocus
            onEscape={handleEscape}
            onCloseAfterSelection={handleClose}
            singleSelect={
              facetConfig.singleSelect || facetConfig.type === 'bool'
            }
            facetConfig={facetConfig}
          />
        </Popover>
      </Box>

      {/* Delete button */}
      <Box>
        <IconButton
          size="tiny"
          variant="squared"
          onClick={onDelete}
          tooltip={t('RemoveFilter', { facet: facetConfig.label })}
          color="default"
          iconName={uiIcons.actions.close}
          data-automation-id={`inline-filter-edit-delete-button-${clause.facet}`}
        />
      </Box>
    </Stack>
  );
};
